/*
 * Copyright Mimic Networks, Inc. 2025.
 */

import { Modal } from 'antd';

import { InstallerToken } from '@/client';
import { useMessage } from '@/hooks/message';
import { useRevokeInstallerTokenMutation } from '@/hooks/mutation/useRevokeInstallerTokenMutation';
import { useMimicTranslation } from '@/hooks/useMimicTranslation';
import { Container } from '@/primitives/Container';
import { Text } from '@/primitives/Text';
import { Title } from '@/primitives/Title';

type RevokeInstallerTokenModalProps = {
  installerToken: InstallerToken | undefined;
  onCancelInstallerTokenRevocation: () => void;
};

export function RevokeInstallerTokenModal({
  installerToken,
  onCancelInstallerTokenRevocation: onCancelInstallerTokenDelete,
}: RevokeInstallerTokenModalProps) {
  const { t } = useMimicTranslation('installerTokens');

  const [message] = useMessage();

  const { mutate: revokeInstallerToken, isPending } = useRevokeInstallerTokenMutation({
    onError: () => {
      message.error(t('revocationError'));
    },
    onSuccess: () => {
      message.success(t('tokenRevoked'));
      closeDeleteDialog();
    },
  });

  const onInstallerTokenDelete = () => {
    revokeInstallerToken(installerToken!);
  };

  const closeDeleteDialog = () => {
    onCancelInstallerTokenDelete();
  };

  return (
    <Modal
      open={Boolean(installerToken)}
      onCancel={onCancelInstallerTokenDelete}
      onOk={onInstallerTokenDelete}
      cancelText={t('confirmDelete.cancel')}
      cancelButtonProps={{ disabled: isPending }}
      okText={t('confirmDelete.ok')}
      okButtonProps={{ danger: true, loading: isPending }}
    >
      <Container padding="sm">
        <Title level={4}>{t('confirmDelete.title')}</Title>
        <Text>{t('confirmDelete.body1')}</Text>
      </Container>
    </Modal>
  );
}
